/**
 * DashboardPage — Tableau de bord principal
 *
 * Affiche les statistiques du site (thèmes, catégories, fiches, clients),
 * des actions rapides (admin) et une grille des catégories.
 * Aligné sur le design du site PHP (astro.arketyp.fr).
 *
 * Page d'accueil après connexion.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import type { Theme, Category } from '../types/courses.js';
import type { ClientsResponse } from '../types/clients.js';

/** Couleurs d'arrière-plan pour les cartes de stats */
const STAT_COLORS = [
  { bg: '#f0f9ff', fg: '#0369a1' },   // bleu — thèmes
  { bg: '#f0fdf4', fg: '#16a34a' },   // vert — catégories
  { bg: '#FBF6EC', fg: colors.accent },   // doré — fiches
  { bg: '#fef2f2', fg: '#dc2626' },   // rouge — clients
] as const;

/** Données de statistiques */
interface Stats {
  themes: number;
  categories: number;
  fiches: number;
  clients: number;
}

/** Catégorie enrichie avec nom du thème et icône */
interface CategoryWithTheme extends Category {
  themeId: number;
  themeNom: string;
  themeIcone: string | null;
}

export function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats>({ themes: 0, categories: 0, fiches: 0, clients: 0 });
  const [allCategories, setAllCategories] = useState<CategoryWithTheme[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    setIsLoading(true);

    try {
      // Charger thèmes et clients en parallèle
      const [themesRes, clientsRes] = await Promise.all([
        apiFetch<Theme[]>('/themes'),
        apiFetch<ClientsResponse>('/clients?page=1&limit=1'),
      ]);

      const themes = themesRes.success && themesRes.data ? themesRes.data : [];
      const clientsTotal = clientsRes.success && clientsRes.data ? clientsRes.data.pagination.total : 0;

      const totalCategories = themes.reduce((sum, t) => sum + t._count.categories, 0);

      // Charger les catégories de chaque thème en parallèle
      const catPromises = themes.map(async (theme) => {
        const res = await apiFetch<Category[]>(`/themes/${theme.id}/categories`);
        if (res.success && res.data) {
          return res.data.map((cat) => ({
            ...cat,
            themeId: theme.id,
            themeNom: theme.nom,
            themeIcone: theme.icone,
          }));
        }
        return [];
      });

      const catResults = await Promise.all(catPromises);
      const cats = catResults.flat();
      const totalFiches = cats.reduce((sum, c) => sum + c._count.fiches, 0);

      setStats({
        themes: themes.length,
        categories: totalCategories,
        fiches: totalFiches,
        clients: clientsTotal,
      });
      setAllCategories(cats);
    } catch {
      // Données partielles en cas d'erreur — on affiche ce qu'on a
    } finally {
      setIsLoading(false);
    }
  }

  const statCards = [
    { label: 'Thèmes', value: stats.themes, icon: 'bi-layers', href: '/courses' },
    { label: 'Catégories', value: stats.categories, icon: 'bi-bookmark', href: '/courses' },
    { label: 'Fiches de cours', value: stats.fiches, icon: 'bi-file-text', href: '/courses' },
    { label: 'Clients', value: stats.clients, icon: 'bi-people', href: '/clients' },
  ];

  const quickActions = [
    { label: 'Nouvelle fiche', icon: 'bi-plus-circle', href: '/cours/nouveau', admin: true },
    { label: 'Nouveau client', icon: 'bi-person-plus', href: '/clients/nouveau', admin: false },
    { label: 'Générer un document', icon: 'bi-file-earmark-zip', href: '/generator', admin: false },
  ];

  return (
    <div style={{ maxWidth: 1000, width: '100%' }}>
      {/* En-tête */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="bi bi-house-door" style={{ fontSize: 22, color: colors.accent }} />
          <h1
            style={{
              margin: 0,
              fontSize: '1.75rem',
              fontWeight: 600,
              color: colors.text,
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            Tableau de bord
          </h1>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: colors.textMid, marginLeft: 34 }}>
          Bienvenue{user?.nom ? `, ${user.nom}` : ''} — Astrologie Humaniste Évolutive
        </p>
      </div>

      {/* Statistiques — cartes cliquables */}
      <div className="grid-4" style={{ marginBottom: 32 }}>
        {statCards.map((stat, i) => {
          const palette = STAT_COLORS[i]!;
          return (
            <Link
              key={stat.label}
              to={stat.href}
              style={{
                display: 'block',
                background: '#fff',
                borderRadius: 10,
                padding: '1.25rem',
                border: `1px solid ${colors.border}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                textDecoration: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = palette.fg;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.3rem',
                  marginBottom: 12,
                  background: palette.bg,
                  color: palette.fg,
                }}
              >
                <i className={stat.icon} />
              </div>
              <div
                style={{
                  fontSize: '1.6rem',
                  fontWeight: 700,
                  color: colors.text,
                  lineHeight: 1.2,
                }}
              >
                {isLoading ? '—' : stat.value}
              </div>
              <div style={{ fontSize: 13, color: colors.textMid, marginTop: 4 }}>
                {stat.label}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Actions rapides */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <i className="bi bi-lightning" style={{ fontSize: 18, color: colors.accent }} />
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: colors.text }}>
            Actions rapides
          </h2>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {quickActions
            .filter((a) => !a.admin || isAdmin)
            .map((action) => (
              <Link
                key={action.label}
                to={action.href}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: '#fff',
                  color: colors.text,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.accent;
                  e.currentTarget.style.background = `${colors.accent}0a`;
                  e.currentTarget.style.color = colors.text;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.color = colors.text;
                }}
              >
                <i className={`bi ${action.icon}`} style={{ fontSize: 16, color: colors.accent }} />
                {action.label}
              </Link>
            ))}
        </div>
      </div>

      {/* Toutes les catégories */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <i className="bi bi-grid-3x3-gap" style={{ fontSize: 18, color: colors.accent }} />
          <h2
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 600,
              color: colors.text,
            }}
          >
            Toutes les catégories
          </h2>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
            Chargement...
          </div>
        ) : allCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
            Aucune catégorie disponible.
          </div>
        ) : (
          <div className="grid-4">
            {allCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/courses/${cat.themeId}`}
                style={{
                  display: 'block',
                  background: '#fff',
                  borderRadius: 10,
                  padding: '1.25rem',
                  border: `1px solid ${colors.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  textDecoration: 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.accent;
                  e.currentTarget.style.boxShadow = `0 2px 8px ${colors.accent}1f`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.3rem',
                    marginBottom: 12,
                    background: colors.text,
                    color: colors.accent,
                  }}
                >
                  <i className={cat.icone ?? 'bi-file-text'} />
                </div>
                <div
                  style={{
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    color: colors.text,
                    marginBottom: 4,
                  }}
                >
                  {cat.nom}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 2 }}>
                  {cat.themeNom}
                </div>
                <div style={{ fontSize: 13, color: colors.accent, fontWeight: 500 }}>
                  {cat._count.fiches} fiche{cat._count.fiches > 1 ? 's' : ''}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
